import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIRECTIONS = [
  { id: "canada-japan", label: "Canada → Japan", sub: "We are a Canadian organization heading east." },
  { id: "japan-canada", label: "Japan → Canada", sub: "We are a Japanese organization heading west." },
  { id: "both", label: "Both directions", sub: "Work flows across the Pacific in both directions." },
  { id: "unsure", label: "Not sure yet", sub: "We are still finding the shape of the question." },
];

const OFFICES = [
  {
    city: "Tokyo Office", testId: "office-tokyo",
    address: "Shibuya City, Tokyo 150-0002, Japan", phone: "+81 3-6822-4170",
    note: "Serving Japan-based clients and visitors from Canada.",
  },
  {
    city: "Toronto Office", testId: "office-toronto",
    address: "1 Yonge Street, Suite 1801, Toronto, ON M5E 1E5, Canada", phone: "+1 (416) 555-0184",
    note: "Serving Ontario's manufacturing corridor and Japan-bound organizations.",
  },
];

const inputCls = "w-full bg-navy border border-navy-border rounded-sm px-4 py-3 text-sm text-cream-surface placeholder:text-silver/60 focus:outline-none focus:border-coral-light transition-colors duration-300";
const labelCls = "block text-xs font-mono tracking-[0.16em] text-silver mb-2 uppercase";

export default function Contact() {
  const [searchParams] = useSearchParams();
  const station = searchParams.get("station");
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    direction: "", industry: "", inquiry_type: "",
    message: station ? `Service map station: ${station}\n\n` : "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.direction) {
      toast.error("Please tell us where you are going first.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your inquiry has been received. We reply within two business days.");
      setForm({ name: "", email: "", company: "", phone: "", direction: "", industry: "", inquiry_type: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-cream pt-16">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-16">
        <Reveal>
          <Overline>CONTACT</Overline>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy">
            Where are you going?
          </h1>
        </Reveal>
        <Reveal delay={0.08}>
          <div data-testid="direction-selector" className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DIRECTIONS.map((d) => (
              <button
                key={d.id}
                type="button"
                data-testid={`direction-${d.id}`}
                onClick={() => setForm({ ...form, direction: d.id })}
                className={`text-left p-6 rounded-sm border transition-all duration-300 ${
                  form.direction === d.id
                    ? "border-coral bg-coral-muted"
                    : "border-sand bg-cream-surface hover:border-navy/40"
                }`}
              >
                <span className={`block w-3 h-3 rounded-full border-2 mb-4 ${
                  form.direction === d.id ? "border-coral bg-coral" : "border-charcoal/30"
                }`} />
                <span className="font-serif text-xl text-navy">{d.label}</span>
                <span className="block mt-2 text-xs leading-relaxed text-charcoal/60">{d.sub}</span>
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="relative overflow-hidden bg-navy text-cream-surface">
        <img
          src="https://images.pexels.com/photos/1029604/pexels-photo-1029604.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1600"
          alt="" aria-hidden="true" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/92" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28">
          <Reveal>
            <div className="flex items-center gap-4 mb-12">
              <span className="h-px w-10 bg-coral-light" />
              <p className="text-xs uppercase tracking-[0.24em] font-mono text-coral-light">START THE CONVERSATION</p>
              <span className="h-px w-10 bg-coral-light" />
            </div>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 space-y-6">
              {OFFICES.map((o, i) => (
                <Reveal key={o.city} delay={i * 0.08}>
                  <div data-testid={o.testId} className="bg-navy-surface/80 backdrop-blur border border-navy-border rounded-sm p-8">
                    <h2 className="font-serif text-2xl text-cream-surface">{o.city}</h2>
                    <p className="mt-4 flex items-start gap-3 text-sm text-[#B7C6E2]">
                      <MapPin size={16} className="mt-0.5 text-coral-light shrink-0" />{o.address}
                    </p>
                    <p className="mt-3 flex items-center gap-3 text-sm text-[#B7C6E2]">
                      <Phone size={16} className="text-coral-light shrink-0" />{o.phone}
                    </p>
                    <p className="mt-4 text-xs leading-relaxed text-silver border-t border-navy-border pt-4">{o.note}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.16}>
                <a
                  href="mailto:hello@rutenborealis.com"
                  data-testid="email-contact-card"
                  className="group flex items-center gap-4 bg-navy-surface/80 backdrop-blur border border-navy-border rounded-sm p-8 transition-colors duration-300 hover:border-coral-light/60"
                >
                  <Mail size={20} className="text-coral-light shrink-0" />
                  <div>
                    <p className="font-serif text-xl text-cream-surface">Prefer email?</p>
                    <p className="mt-1 text-sm text-[#B7C6E2] group-hover:text-cream-surface transition-colors duration-300">
                      hello@rutenborealis.com
                    </p>
                  </div>
                </a>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <form onSubmit={submit} data-testid="contact-form" className="bg-navy-surface/80 backdrop-blur border border-navy-border rounded-sm p-8 sm:p-10 space-y-5">
                  <h2 className="font-serif text-2xl text-cream-surface">Tell us about the crossing</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="cf-name">Name *</label>
                      <input id="cf-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-email">Email *</label>
                      <input id="cf-email" data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-company">Company</label>
                      <input id="cf-company" data-testid="contact-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company or organization" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-phone">Phone</label>
                      <input id="cf-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 … / +81 …" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-industry">Industry</label>
                      <select id="cf-industry" data-testid="contact-industry" value={form.industry} onChange={set("industry")} className={inputCls}>
                        <option value="">Select…</option>
                        {["Manufacturing", "Technology", "Food & Craft", "Heritage & Consumer Brands", "Education", "Other"].map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="cf-type">Inquiry type *</label>
                      <select id="cf-type" data-testid="contact-inquiry-type" required value={form.inquiry_type} onChange={set("inquiry_type")} className={inputCls}>
                        <option value="">Select…</option>
                        {["Interpretation", "Cultural Consulting", "Education / Workshops", "Seminars", "Store / Products", "Other"].map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-message">Message *</label>
                    <textarea id="cf-message" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} placeholder="What are you trying to make happen between Japan and Canada?" />
                  </div>
                  <p className="text-xs text-silver">
                    Direction: <span className="font-medium text-cream-surface/80">{DIRECTIONS.find((d) => d.id === form.direction)?.label || "not selected yet"}</span>
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={sending}
                    className="w-full sm:w-auto bg-gold hover:bg-gold-dark disabled:opacity-60 text-navy font-semibold px-10 py-3.5 rounded-full transition-colors duration-300"
                  >
                    {sending ? "Sending…" : "Send inquiry"}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
